import { clsx } from 'clsx'
import { Button, CaretDownIcon, CaretUpIcon } from '@tovala/component-library'
import {
  Listing,
  MealSummary,
  useOrderEstimate,
  UserV1,
} from '@tovala/browser-apis-combinedapi'
import { getMealPricing } from '@tovala/browser-apis-meals'

import { ReactNode, useEffect, useState } from 'react'

import { formatCentsToDollars } from 'utils/currency'
import {
  getMealImageURL,
  getMealMultipleServingPriceSummary,
} from 'utils/meals'
import { UserTerm } from 'types/internal'

import { useUser } from 'contexts/user'
import { useUserTermStatuses } from 'hooks/terms'
import Collapsible, {
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/common/Collapsible'
import ErrorDisplay from 'components/common/ErrorDisplay'
import Sidebar, { SidebarBody, SidebarHeader } from 'components/common/Sidebar'
import { SmallMealImage } from 'components/common/MealImage'
import TermSkippedSidebarBody from '../TermSkippedSidebarBody'
import TermSoldOutSidebarBody from '../TermSoldOutSIdebarBody'
import { track } from 'utils/analytics'
import { events, sourceIDs } from 'analytics/events'
import { getTotalCreditsCents } from 'utils/user'
import { getOrderSummaryDetails, isTermDelivering } from 'utils/terms'

type OrderSummaryProps = {
  hasOrderAgainMealsSelected: boolean
  listings: ReactNode[]
  meals: ReactNode[]
  onClickPickMeals(): void
  onClickSkipAdjust(): void
  onCloseSidebar(): void
  orderStatus: ReactNode
  selectedUserTerm: UserTerm
  showOrderUpgrade: boolean
}

const OrderSummarySidebar = ({
  onCloseSidebar,
  selectedUserTerm,
  ...orderSummaryProps
}: OrderSummaryProps) => {
  return (
    <Sidebar onCloseSidebar={onCloseSidebar}>
      <SidebarHeader
        onClickClose={() => {
          track(events.ORDER_SUMMARY_DISMISS)
          onCloseSidebar()
        }}
      >
        Order Summary
      </SidebarHeader>

      <SidebarBody>
        <div className="space-y-10 px-6 pb-20 pt-10 md:space-y-4 md:px-4">
          {selectedUserTerm.isStaticSkipped ? (
            <TermSoldOutSidebarBody onClickMyOrders={onCloseSidebar} />
          ) : selectedUserTerm.isSkipped ? (
            <TermSkippedSidebarBody selectedUserTerm={selectedUserTerm} />
          ) : (
            <OrderSummary
              {...orderSummaryProps}
              onCloseSidebar={onCloseSidebar}
              selectedUserTerm={selectedUserTerm}
            />
          )}
        </div>
      </SidebarBody>
    </Sidebar>
  )
}

export default OrderSummarySidebar

const OrderSummary = ({
  hasOrderAgainMealsSelected,
  listings,
  meals,
  onClickPickMeals,
  onClickSkipAdjust,
  onCloseSidebar,
  orderStatus,
  selectedUserTerm,
  showOrderUpgrade,
}: OrderSummaryProps) => {
  const { selectedSubTerm } = selectedUserTerm || {}

  return (
    <>
      <div className="flex justify-end">
        <Button
          buttonStyle="stroke"
          disabled={showOrderUpgrade}
          onClick={() => {
            onClickSkipAdjust()
            track(events.SKIP_ADJUST_CTA, {
              source_id: sourceIDs.ORDER_SUMMARY,
            })
            onCloseSidebar()
          }}
          size="medium"
        >
          Skip/Adjust
        </Button>
      </div>

      {selectedSubTerm ? (
        <>
          <EstimatedTotal selectedUserTerm={selectedUserTerm} />

          <div>
            {meals.length === 0 ? (
              <div className="rounded-xl bg-white pb-16 pt-8 text-center">
                <p className="mx-auto max-w-[188px] text-k/20_110">
                  Choose meals or leave it to us!
                </p>
                <p className="mx-auto mt-4 max-w-[280px] text-sm text-grey-9">
                  Choose {selectedUserTerm.subscriptionType?.maxSelections}{' '}
                  meals from our menu to fill your box. Our chefs will add their
                  selections for any meals you do not choose.
                </p>
                <div className="mt-8 flex justify-center">
                  <Button
                    buttonStyle="dark"
                    onClick={() => {
                      track(events.ORDER_SUMMARY_PICK_MEALS)

                      onClickPickMeals()
                    }}
                    size="large"
                  >
                    Pick Meals
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">{meals}</div>
            )}

            {listings.length > 0 && (
              <div className="mt-4 border-t border-grey-3 pt-4">
                <div className="space-y-4">{listings}</div>
              </div>
            )}

            {hasOrderAgainMealsSelected && (
              <p className="pt-3 text-k/14_120 text-grey-10">
                Ingredients may have changed since your last order. Please
                confirm ingredients and allergens before ordering.
              </p>
            )}
          </div>

          {orderStatus}
        </>
      ) : (
        <ErrorDisplay
          helpToFix="Please refresh the page."
          why="We couldn't load your order summary details due to a technical issue on our end."
        />
      )}
    </>
  )
}

const EstimatedTotal = ({
  selectedUserTerm,
}: {
  selectedUserTerm: UserTerm
}) => {
  const { user } = useUser()

  const { data: orderEstimate } = useOrderEstimate({
    termID: isTermDelivering(selectedUserTerm)
      ? selectedUserTerm.termID
      : undefined,
    userID: user.id,
  })
  const isFirstAvailableUnskippedMenu = useIsFirstAvailableUnskippedMenu({
    termID: selectedUserTerm.termID,
    user,
  })

  const accountBalanceCents = getTotalCreditsCents({ user })
  const accountBalanceApplies =
    accountBalanceCents > 0 && isFirstAvailableUnskippedMenu

  const isLegacyFreeShipping =
    selectedUserTerm.selectedSubTerm?.subscriptionPricing
      ?.shippingAmountCents === 0

  const orderSummaryDetails = getOrderSummaryDetails({
    accountBalanceCents: accountBalanceApplies ? accountBalanceCents : 0,
    isLegacyFreeShipping,
    orderEstimate,
    selectedSubscription: selectedUserTerm.subscriptionType,
  })

  if (!orderSummaryDetails) {
    return null
  }

  const {
    estimatedDiscountCents,
    estimatedSubtotalCents,
    estimatedTotalCents,
    isDiscounted,
    isFreeShipping,
    isMultipleDiscounts,
    additionalCostCents,
    baseMealCents,
    mealPlanCents,
    shippingCents,
  } = orderSummaryDetails

  return (
    <div className="rounded-lg bg-grey-2">
      <Collapsible>
        {({ open }) => {
          return (
            <>
              <CollapsibleTrigger
                className="w-full p-6 md:px-4"
                onClick={() => {
                  if (!open) {
                    track(events.ORDER_SUMMARY_TOTAL_EXPAND)
                  }
                }}
              >
                <EstimatedTotalTrigger isOpen={open}>
                  {isDiscounted && (
                    <span className="text-green">
                      {estimatedSubtotalCents > 0
                        ? `${formatCentsToDollars(estimatedSubtotalCents)}`
                        : 'Free'}{' '}
                    </span>
                  )}
                  <span
                    className={clsx({
                      'line-through': isDiscounted,
                    })}
                  >
                    {formatCentsToDollars(estimatedTotalCents)}
                  </span>
                </EstimatedTotalTrigger>
              </CollapsibleTrigger>

              <CollapsibleContent open={open}>
                <div className="px-6 pb-6 md:px-4">
                  <EstimatedTotalDetails
                    additionalCostCents={additionalCostCents}
                    baseMealCents={baseMealCents}
                    discountCents={isDiscounted ? estimatedDiscountCents : 0}
                    isDefaultMealPlan={
                      selectedUserTerm.selectedSubscriptionTypeIsDefault
                    }
                    isFreeShipping={isFreeShipping}
                    isLegacyFreeShipping={isLegacyFreeShipping}
                    isMultipleDiscounts={isMultipleDiscounts}
                    maxSelections={{
                      default:
                        user.subscription.subscriptionType?.maxSelections ?? 0,
                      selected:
                        selectedUserTerm.subscriptionType?.maxSelections ?? 0,
                    }}
                    mealPlanCents={mealPlanCents}
                    shippingCents={shippingCents}
                  />
                </div>
              </CollapsibleContent>
            </>
          )
        }}
      </Collapsible>
    </div>
  )
}

const EstimatedTotalTrigger = ({
  children,
  isOpen,
}: {
  children: ReactNode
  isOpen: boolean
}) => {
  return (
    <div className="flex items-center justify-between text-k/20_125 md:text-k/18_120">
      <div className="flex items-center justify-between space-x-2">
        <h3 className="text-h/18_110">Estimated Total</h3>

        {isOpen ? (
          <div className="h-6 w-6">
            <CaretUpIcon />
          </div>
        ) : (
          <div className="h-6 w-6">
            <CaretDownIcon />
          </div>
        )}
      </div>

      <div>{children}</div>
    </div>
  )
}

const EstimatedTotalDetails = ({
  additionalCostCents,
  discountCents,
  mealPlanCents,
  isDefaultMealPlan,
  isFreeShipping,
  isMultipleDiscounts,
  maxSelections,
  baseMealCents,
  shippingCents,
}: {
  additionalCostCents: number
  discountCents: number
  mealPlanCents: number
  isDefaultMealPlan: boolean
  isFreeShipping: boolean
  isLegacyFreeShipping: boolean
  isMultipleDiscounts: boolean
  maxSelections: {
    default: number
    selected: number
  }
  baseMealCents: number
  shippingCents: number
}) => {
  return (
    <>
      <div className="space-y-6 text-k/16_125">
        <div className="space-y-2 border-t border-grey-4 pt-6">
          <div className="flex justify-between gap-x-4">
            <span>Meal Plan</span>
            {mealPlanCents && (
              <span>{formatCentsToDollars(mealPlanCents)}</span>
            )}
          </div>
          <p className="text-k/14_120 text-grey-8">
            <span
              className={clsx({
                'block line-through': !isDefaultMealPlan,
              })}
            >
              {maxSelections.default} Standard Meals
            </span>
            {!isDefaultMealPlan && (
              <span> {maxSelections.selected} Standard Meals</span>
            )}
            , {formatCentsToDollars(baseMealCents)} each
          </p>
        </div>

        {additionalCostCents > 0 && (
          <div className="space-y-2">
            <div className="flex justify-between gap-x-4">
              <span className="block max-w-[220px]">
                Extras, Premium, Two-Packs, or Family Meals
              </span>
              <span>+{formatCentsToDollars(additionalCostCents)}</span>
            </div>
            <p className="text-k/14_120 text-grey-8">
              Two-Packs and Family Meals are larger portions. You still use just
              one spot in your box, but pricing varies based on serving size.
            </p>
          </div>
        )}

        <div className="flex justify-between border-t border-grey-4 pt-6">
          <span>Shipping</span>
          <span>
            {shippingCents > 0 ? (
              <span>
                {isFreeShipping && <span className="text-green">Free</span>}{' '}
                <span
                  className={clsx({
                    'line-through': isFreeShipping,
                  })}
                >
                  {formatCentsToDollars(shippingCents)}
                </span>
              </span>
            ) : (
              'Free'
            )}
          </span>
        </div>

        {discountCents > 0 && (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span>Discount</span>
              <span className="text-green">
                -{formatCentsToDollars(discountCents)}
                {isMultipleDiscounts && (
                  <span className="text-green"> (multiple discounts)</span>
                )}
              </span>
            </div>
            <p className="text-k/14_120 text-grey-8">
              Any Tovala Cash or discounts associated with your account will be
              applied to your next order.
            </p>
          </div>
        )}
      </div>
    </>
  )
}

export const OrderSummaryExtra = ({
  listing,
  stepper,
}: {
  listing: Listing
  stepper: ReactNode
}) => {
  return (
    <OrderSummaryItem
      imageURL={listing.imageURL}
      price={`${formatCentsToDollars(listing.priceCents)}`}
      stepper={stepper}
      title={listing.title}
    />
  )
}

export const OrderSummaryMeal = ({
  meal,
  mealStepper,
}: {
  meal: MealSummary
  mealStepper: ReactNode
}) => {
  const { servings, servingPrice, surchargePrice } = getMealPricing({ meal })

  return (
    <OrderSummaryItem
      imageURL={getMealImageURL(meal)}
      price={
        servings === 2 || servings === 4
          ? getMealMultipleServingPriceSummary({ servings, servingPrice })
          : surchargePrice
            ? `+${surchargePrice}`
            : null
      }
      stepper={mealStepper}
      subtitle={meal.subtitle}
      title={meal.title}
    />
  )
}

const OrderSummaryItem = ({
  imageURL,
  price,
  stepper,
  subtitle,
  title,
}: {
  imageURL: string
  price: string | null
  stepper: ReactNode
  subtitle?: string
  title: ReactNode | string
}) => {
  return (
    <div className="flex items-center justify-between space-x-4">
      <div className="flex items-center space-x-4">
        <div className="shrink-0">
          <SmallMealImage imageURL={imageURL} />
        </div>

        <div className="space-y-1">
          {price && (
            <p className="text-k/13_120 text-[#6F6F69] md:text-k/12_120">
              {price}
            </p>
          )}
          <h3 className="text-k/16_125 md:text-k/14_120">{title}</h3>
          {subtitle && (
            <p className="text-k/13_120 text-grey-10 md:text-k/12_120">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      <div className="flex shrink-0 justify-end">{stepper}</div>
    </div>
  )
}

function useIsFirstAvailableUnskippedMenu({
  termID,
  user,
}: {
  termID: number
  user: UserV1
}) {
  const { data: termStatuses = [] } = useUserTermStatuses({ user })

  const firstUnskippedTermID = termStatuses.find(
    (term) => !term.isSkipped,
  )?.termID

  return termID === firstUnskippedTermID
}

export interface OrderSummarySidebarStatus {
  isOpen: boolean
  openSource: 'selectionsConfirmation' | undefined
}

export function useOrderSummarySidebar({
  hasSelectedAllMeals,
}: {
  hasSelectedAllMeals: boolean
}) {
  // We keep track of where order summary was opened from for sources where we need
  // some special logic, like showing the "My Orders" button on the coaching bar
  // when summary was opened from the selections confirmed dialog so the user can't
  // get stuck in a loop of opening summary and seeing the confirmation dialog
  const [sidebarStatus, setSidebarStatus] = useState<OrderSummarySidebarStatus>(
    {
      isOpen: false,
      openSource: undefined,
    },
  )

  function closeOrderSummary() {
    setSidebarStatus((prevState) => {
      return {
        isOpen: false,
        openSource: prevState.openSource,
      }
    })
  }

  function openOrderSummary(
    openSource: OrderSummarySidebarStatus['openSource'] = undefined,
  ) {
    setSidebarStatus((prevState) => {
      return {
        isOpen: true,
        openSource:
          openSource !== undefined ? openSource : prevState.openSource,
      }
    })
  }

  useEffect(() => {
    if (!hasSelectedAllMeals) {
      setSidebarStatus((prevState) => {
        return {
          isOpen: prevState.isOpen,
          openSource: undefined,
        }
      })
    }
  }, [hasSelectedAllMeals])

  return {
    closeOrderSummary,
    openOrderSummary,
    orderSummarySidebarStatus: sidebarStatus,
  }
}
